import React, { useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";

// **Headings and Paragraphs with Animations**
export const MotionHeading = (props) => (
  <motion.h1
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    style={{ color: "#4caf50" }}
    {...props}
  />
);

export const MotionSubheading = (props) => (
  <motion.h2
    initial={{ y: -50, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ type: "spring", stiffness: 150 }}
    style={{ color: "#4caf50" }}
    {...props}
  />
);

export const MotionParagraph = (props) => (
  <motion.p
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.4 }}
    {...props}
  />
);

// **Collapsible Section with Animation**
const CollapsibleContainer = styled.div`
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 5px;
  background-color: #fce6c3;
  margin-bottom: 20px;
`;

export const CollapsibleSection = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <CollapsibleContainer>
      <h3 onClick={() => setIsOpen(!isOpen)} style={{ cursor: "pointer" }}>
        {title} {isOpen ? "▲" : "▼"}
      </h3>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={
          isOpen ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }
        }
        transition={{ duration: 0.5, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        {isOpen && <div>{children}</div>}
      </motion.div>
    </CollapsibleContainer>
  );
};

// Progress Bar
const ProgressBarContainer = styled.div`
  background-color: #ddd;
  border-radius: 5px;
  width: 100%;
  height: 20px;
  margin: 10px 0;
  overflow: hidden;
`;

const ProgressIndicator = styled.div`
  background-color: #4caf50;
  height: 100%;
  width: ${(props) => `${props.progress}%`};
  border-radius: 5px;
`;

export const ProgressBar = (
  { progress = 0 } // default value set to 0
) => (
  <ProgressBarContainer>
    <motion.div
      initial={{ width: "0%" }}
      animate={{ width: `${progress}%` || "100%" }} // fallback to "100%" if undefined
      transition={{ duration: 1, ease: "easeInOut" }}
      style={{
        backgroundColor: "#4caf50",
        height: "100%",
        borderRadius: "5px",
      }}
    />
  </ProgressBarContainer>
);

export const Button = ({ children, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    style={{
      backgroundColor: "#4caf50",
      color: "white",
      padding: "10px 20px",
      borderRadius: "5px",
      border: "none",
      cursor: "pointer",
    }}
    onClick={onClick}
  >
    {children}
  </motion.button>
);

// Styled horizontal line with motion
export const MotionHorizontalLine = (props) => (
  <motion.div
    initial={{ scaleX: 0 }}
    animate={{ scaleX: 1 }}
    transition={{ duration: 0.6 }}
    style={{
      width: "100%", // Full width of the parent container
      height: "4px", // Height of the line
      maxWidth: "800px", // Optional to keep the line from getting too wide
      backgroundColor: "#f5b041", // Line color
      borderRadius: "4px", // Rounded edges
      margin: "20px auto", // Centering with margin auto
      transformOrigin: "left", // Animation starts from the left
    }}
    {...props}
  />
);

const FullScreenBackdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

// Styled full-screen image
const FullScreenImage = styled(motion.img)`
  max-width: 90%;
  max-height: 90%;
  border-radius: 12px;
  cursor: zoom-out;
`;

// Styled container for images displayed side by side
export const ImageGallery = styled.div`
  display: flex;
  justify-content: center;
  align-items: center; /* Center vertically if needed */
  gap: 24px; /* Space between images */
  flex-wrap: wrap; /* Allows wrapping if images don't fit */
  margin: 10px 0 30px; /* Space above and below */
  width: 100%; /* Ensure the container takes the full width */
  max-width: 1200px; /* Optionally limit the gallery size */
  padding: 0 1rem; /* Add some padding on the sides to avoid touching screen edges */
`;

export const StyledImage = styled(motion.img)`
  width: 200px; /* Larger width */
  height: 400px; /* Keep square ratio */
  border-radius: 12px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.87); /* Subtle shadow */
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.3); /* Zoom effect */
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.6); /* Larger shadow for a more dramatic effect */
  }
`;

export const MotionImageGallery = ({ images }) => (
  <ImageGallery>
    {images.map((image, index) => (
      <StyledImage
        key={index}
        src={image.src}
        alt={image.alt}
        whileHover={{ scale: 1.1 }}
      />
    ))}
  </ImageGallery>
);

export const ExpandableImageGallery = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleCloseFullScreen = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <ImageGallery>
        {images.map((image, index) => (
          <StyledImage
            key={index}
            src={image.src}
            alt={image.alt}
            onClick={() => handleImageClick(image)}
            whileHover={{ scale: 1.1 }}
          />
        ))}
      </ImageGallery>
      {selectedImage && (
        <FullScreenBackdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseFullScreen}
        >
          <FullScreenImage src={selectedImage.src} alt={selectedImage.alt} />
        </FullScreenBackdrop>
      )}
    </>
  );
};

// **MDX Components Exported for MDXProvider**
const mdxComponents = {
  h1: MotionHeading,
  h2: MotionSubheading,
  p: MotionParagraph,
  hr: MotionHorizontalLine, // Add horizontal line here
  CollapsibleSection: CollapsibleSection,
  ProgressBar: ProgressBar,
  Button: Button,
};

export default mdxComponents;
